import React from 'react';
import { Edit, Shield, FileText, Power, PowerOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import { formatLicensePlate } from '../mocks/system-parameters.mock';
import type { Vehicle } from '../types/system-parameters.types';

interface VehiclesTableProps {
  vehicles: Vehicle[];
  onEdit: (vehicle: Vehicle) => void;
  onDelete: (vehicle: Vehicle) => void;
}

type SortField = 'plate' | 'brand' | 'model' | 'year' | 'insuranceExpiry';

const isExpiringSoon = (dateString: string): boolean => {
  const expiryDate = new Date(dateString);
  const today = new Date();
  const diffTime = expiryDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 30 && diffDays > 0;
};

export const VehiclesTable: React.FC<VehiclesTableProps> = ({
  vehicles,
  onEdit,
  onDelete
}) => {
  const fieldLabels: Record<SortField, string> = {
    plate: 'Placa',
    brand: 'Marca',
    model: 'Modelo',
    year: 'Año',
    insuranceExpiry: 'SOAT'
  };

  const sortValueExtractor = (vehicle: Vehicle, field: SortField): any => {
    switch (field) {
      case 'plate':
        return vehicle.additionalData.plate.toLowerCase();
      case 'brand':
        return vehicle.additionalData.brand.toLowerCase();
      case 'model':
        return vehicle.additionalData.model.toLowerCase();
      case 'year':
        return vehicle.additionalData.year;
      case 'insuranceExpiry':
        return vehicle.additionalData.insuranceExpiry ? new Date(vehicle.additionalData.insuranceExpiry) : new Date(0);
      default:
        return vehicle.additionalData.plate;
    }
  };

  const tableData = useTableData<Vehicle, SortField>(
    {
      data: vehicles,
      defaultSortField: 'plate',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (vehicles.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay vehículos</h3>
        <p className="text-gray-500">No se encontraron vehículos con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="plate"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Placa
              </SortableHeader>
              <SortableHeader
                field="brand"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Marca
              </SortableHeader>
              <SortableHeader
                field="model"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Modelo
              </SortableHeader>
              <SortableHeader
                field="year"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Año
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Color
              </th>
              <SortableHeader
                field="insuranceExpiry"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                SOAT
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rev. Técnica
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((vehicle) => {
              const isInsuranceExpiring = vehicle.additionalData.insuranceExpiry && isExpiringSoon(vehicle.additionalData.insuranceExpiry);
              const isTechnicalReviewExpiring = vehicle.additionalData.technicalReviewExpiry && isExpiringSoon(vehicle.additionalData.technicalReviewExpiry);

              return (
                <tr key={vehicle.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div>
                      <div className="text-sm font-bold text-gray-900">
                        {formatLicensePlate(vehicle.additionalData.plate)}
                      </div>
                      <div className="text-xs text-gray-400">
                        ID: {vehicle.id}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {vehicle.additionalData.brand}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {vehicle.additionalData.model}
                    </div>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900 font-medium">
                      {vehicle.additionalData.year}
                    </div>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-600">
                      {vehicle.additionalData.color || '-'}
                    </div>
                  </td>

                  <td className="px-4 py-4 text-center">
                    {vehicle.additionalData.insuranceExpiry ? (
                      <div className="flex items-center justify-center gap-2">
                        <Shield className="h-3 w-3 text-gray-500" />
                        <span className={`text-sm ${isInsuranceExpiring ? 'text-orange-600 font-semibold' : 'text-gray-900'}`}>
                          {new Date(vehicle.additionalData.insuranceExpiry).toLocaleDateString('es-PE')}
                        </span>
                        {isInsuranceExpiring && <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">Por vencer</Badge>}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>

                  <td className="px-4 py-4 text-center">
                    {vehicle.additionalData.technicalReviewExpiry ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileText className="h-3 w-3 text-gray-500" />
                        <span className={`text-sm ${isTechnicalReviewExpiring ? 'text-orange-600 font-semibold' : 'text-gray-900'}`}>
                          {new Date(vehicle.additionalData.technicalReviewExpiry).toLocaleDateString('es-PE')}
                        </span>
                        {isTechnicalReviewExpiring && <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">Por vencer</Badge>}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>

                  <td className="px-4 py-4 text-center">
                    <Badge className={vehicle.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {vehicle.isActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <div className="flex gap-2 justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(vehicle)}
                        className="text-xs"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(vehicle)}
                        className={`text-xs ${vehicle.additionalData?.isActive
                          ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                          : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                      >
                        {vehicle.additionalData?.isActive ? (
                          <><PowerOff className="w-3 h-3 mr-1" />Desactivar</>
                        ) : (
                          <><Power className="w-3 h-3 mr-1" />Reactivar</>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};
